import {
  ReactElement,
  cloneElement,
  forwardRef,
  ButtonHTMLAttributes,
  ReactNode,
} from 'react'
import { css, keyframes, styled, CSS } from 'theme'
import { anchorStyles, h4Styles, h5Styles } from '.'

export const ButtonCss = styled('button', {
  // Reset
  'all': 'unset',
  'alignItems': 'normal',
  'userSelect': 'none',
  'cursor': 'pointer',
  'boxShadow': 'none',

  // Custom reset?
  'display': 'inline-flex',
  'justifyContent': 'center',
  'WebkitTapHighlightColor': 'rgba(0,0,0,0)',
  'transitionButton': '$transition$easeOutQuint',
  'border': '1px solid $primaryBase',

  // Custom
  'fontSize': '$14',
  'fontWeight': '$semibold',
  'lineHeight': '$lineHeight$20',
  'borderRadius': '$radii$sm',
  'p': '$size$12 $size$22',
  'backgroundColor': '$primaryBg',
  'color': '$whiteBase',
  'minHeight': 20,
  'minWidth': 20,

  '&:hover': {
    backgroundColor: '$primarySolidHover',
    color: '$whiteBase',
    borderColor: '$primarySolidHover',
  },

  '&:disabled': {
    backgroundColor: '$primaryDisabled',
    color: '$disabledText',
    cursor: 'not-allowed',
    borderColor: '$primaryDisabled',

    svg: {
      path: {
        stroke: '$gray300',
      },
    },
  },

  'variants': {
    size: {
      xs: {
        borderRadius: '$radii$xs',
        p: '$size$4',
      },
      sm: {
        borderRadius: '$radii$xs',
        p: '$size$6 $size$10',
      },
      md: {
        borderRadius: '$radii$sm',
        p: '$size$8 $size$14',
      },
      lg: {
        borderRadius: '$radii$sm',
        p: '$size$10 $size$16',
        ...h5Styles,
        fontWeight: '$normal',
      },
      xl: {
        borderRadius: '$radii$sm',
        p: '$size$12 $size$18',
        ...h4Styles,
        fontWeight: '$normal',
      },
    },
    color: {
      primary: {
        'backgroundColor': '$primaryBg',
        'color': '$whiteBase',
        'borderColor': '$primaryBg',
        'path': {
          stroke: '$whiteBase',
        },
        '&:hover': {
          backgroundColor: '$primaryBgHover',
          borderColor: '$primaryBgHover',
        },
      },
      primaryLight: {
        'backgroundColor': '$primary50',
        'color': '$primaryBg',
        'borderColor': '$primary200',
        'padding': '$12',
        'path': {
          stroke: '$primaryBg',
        },
        '&:hover': {
          backgroundColor: '$whiteBase',
          borderColor: '$primary200',
          color: '$primaryBg',
        },
      },
      secondary: {
        'backgroundColor': '$primaryBgLight',
        'color': '$primaryBase',
        'borderColor': '$primaryBorder',
        'path': {
          stroke: '$primaryBase',
        },
        '&:hover': {
          backgroundColor: '$primaryBg',
          color: 'whiteBase',
          borderColor: '$primaryBg',
          path: {
            stroke: '$whiteBase',
          },
        },
      },
      gray: {
        'backgroundColor': '$gray100',
        'color': '$titleGray',
        'borderColor': '$gray100',
        'path': {
          stroke: '$titleGray',
        },
        '&:hover': {
          backgroundColor: '$gray200',
          color: '$titleGray',
          borderColor: '$gray200',
        },
      },
      white: {
        'backgroundColor': '$whiteBase',
        'color': '$text',
        'borderColor': '$gray300',
        'path': {
          stroke: '$text',
        },
        '&:hover': {
          backgroundColor: '$gray200',
          color: '$titleGray',
        },
      },
      warning: {
        'backgroundColor': '$warning600',
        'color': '$whiteBase',
        'borderColor': '$warning600',
        'path': {
          stroke: '$whiteBase',
        },
        '&:hover': {
          backgroundColor: '$warning700',
          borderColor: '$warning700',
        },
      },
      error: {
        'backgroundColor': '$error600',
        'color': '$whiteBase',
        'borderColor': '$error600',
        'path': {
          stroke: '$whiteBase',
        },
        '&:hover': {
          backgroundColor: '$error700',
          borderColor: '$error700',
        },
      },
      success: {
        'backgroundColor': '$success600',
        'color': '$whiteBase',
        'borderColor': '$success600',
        'path': {
          stroke: '$whiteBase',
        },
        '&:hover': {
          backgroundColor: '$success700',
          borderColor: '$success700',
        },
      },
      dropdown: {
        'fontWeight': '$normal',
        'lineHeight': '$lineHeight$24',
        'borderColor': '$dropdownBorder',
        'borderRadius': '$xs',
        'backgroundColor': '$dropdownBg',
        'color': '$placeholder',
        'py': '$space$10',
        '&:hover': {
          backgroundColor: '$dropdownBg',
          borderColor: '$dropdownBorderFocus',
          color: '$placeholder',
          boxShadow: '$inputShadow',
        },
      },
      errorDropdown: {
        'fontWeight': '$normal',
        'lineHeight': '$lineHeight$24',
        'borderColor': '$inputErrorBorder',
        'borderRadius': '$xs',
        'backgroundColor': '$dropdownBg',
        'color': '$placeholder',
        'py': '$space$10',
        '&:hover': {
          backgroundColor: '$dropdownBg',
          borderColor: '$inputErrorBorder',
          color: '$placeholder',
          boxShadow: '$inputErrorShadow',
        },
      },
      transparent: {},
      black: {},
      disabled: {},
    },
    state: {
      active: {
        'backgroundColor': '$primaryBgActive',
        'color': '$whiteBase',
        'borderColor': '$primaryBgActive',
        '&:hover': {
          backgroundColor: '$primaryBgHover',
          borderColor: '$primaryBgHover',
        },
        '&:active': {
          backgroundColor: '$primaryBgActive',
          borderColor: '$primaryBgActive',
        },
      },
      disabled: {
        '&:disabled': {
          'backgroundColor': '$buttonDisabledBg',
          'color': '$buttonDisabledText',
          'borderColor': '$buttonDisabledBorder',
          '& p': {
            color: '$buttonDisabledText',
          },
        },
      },
      primaryDisabled: {
        '&:disabled': {
          'backgroundColor': '$primaryDisabled',
          'color': '$whiteBase',
          'borderColor': '$primaryDisabled',
          'svg': {
            path: {
              stroke: '$whiteBase',
            },
          },
          '&:hover': {
            backgroundColor: '$primaryDisabled',
            borderColor: '$primaryDisabled',
          },
        },
      },
      grayDisabled: {
        '&:disabled': {
          'backgroundColor': '$disabledBg',
          'color': '$buttonDisabledText',
          'borderColor': '$buttonDisabledBorder',
          'path': {
            stroke: '$disabledHintIcon',
          },
          '&:hover': {
            backgroundColor: '$disabledBg',
            borderColor: '$buttonDisabledBorder',
          },
        },
      },
      errorDisabled: {
        '&:disabled': {
          'backgroundColor': '$primaryDisabled',
          'color': '$whiteBase',
          'borderColor': '$primaryDisabled',
          'path': {
            stroke: '$whiteBase',
          },
          '&:hover': {
            backgroundColor: '$primaryDisabled',
            borderColor: '$primaryDisabled',
          },
        },
      },
      delete: {
        'backgroundColor': '$error600',
        'color': '$whiteBase',
        'borderColor': '$error600',
        '&:hover': {
          backgroundColor: '$error700',
          borderColor: '$error700',
        },
      },
    },
    iconButton: {
      primary: {
        'alignItems': 'center',
        'backgroundColor': '$primary50',
        'borderColor': '$primary200',
        'p': '$space$8',
        'path': {
          stroke: '$primaryBase',
        },
        '&:hover': {
          backgroundColor: '$primary100',
          borderColor: '$primary300',
          path: {
            stroke: '$primaryBgHover',
          },
        },
      },
      secondary: {
        'alignItems': 'center',
        'backgroundColor': '$secondary50',
        'borderColor': '$secondary200',
        'p': '$space$8',
        'path': {
          stroke: '$primaryBase',
        },
        '&:hover': {
          backgroundColor: '$secondary100',
          borderColor: '$secondary300',
          path: {
            stroke: '$primary700',
          },
        },
      },
      warning: {
        'alignItems': 'center',
        'backgroundColor': '$warning50',
        'borderColor': '$warning200',
        'p': '$space$8',
        'path': {
          stroke: '$warning700',
        },
        '&:hover': {
          backgroundColor: '$warning100',
          borderColor: '$warning300',
          path: {
            stroke: '$warning700',
          },
        },
      },
      error: {
        'alignItems': 'center',
        'backgroundColor': '$error50',
        'borderColor': '$error200',
        'p': '$space$8',
        'path': {
          stroke: '$error700',
        },
        '&:hover': {
          backgroundColor: '$error100',
          borderColor: '$error300',
          path: {
            stroke: '$error700',
          },
        },
      },
      success: {
        'alignItems': 'center',
        'backgroundColor': '$success50',
        'borderColor': '$success200',
        'p': '$space$8',
        'path': {
          stroke: '$success700',
        },
        '&:hover': {
          backgroundColor: '$success100',
          borderColor: '$success300',
          path: {
            stroke: '$success700',
          },
        },
      },
      transparent: {
        'alignItems': 'center',
        'backgroundColor': 'transparent',
        'borderColor': '$inputBorder',
        'p': '$space$8',
        'path': {
          stroke: '$text',
        },
        '&:hover': {
          backgroundColor: 'transparent',
          borderColor: '$primaryBase',
          path: {
            stroke: '$primaryBase',
          },
        },
      },
      gray: {
        'alignItems': 'center',
        'backgroundColor': '$gray200',
        'borderColor': '$gray200',
        'p': '$space$8',
        'path': {
          stroke: '$text',
        },
        '&:hover': {
          backgroundColor: '$gray300',
          borderColor: '$gray300',
          path: {
            stroke: '$text',
          },
        },
      },
      black: {
        'alignItems': 'center',
        'backgroundColor': 'transparent',
        'borderColor': '$gray500',
        'p': '$space$8',
        'path': {
          stroke: '$gray500',
        },
        '&:hover': {
          backgroundColor: '$whiteBase',
          borderColor: '$blackBase',
          path: {
            stroke: '$blackBase',
          },
        },
      },
      white: {
        'backgroundColor': '$whiteBase',
        'color': '$gray600',
        'borderColor': '$whiteBase',
        'p': '$space$8',
        'path': {
          stroke: '$gray600',
        },
        '&:hover': {
          backgroundColor: '$gray300',
          borderColor: '$whiteBase',
          color: '$gray700',
          path: {
            stroke: '$gray700',
          },
        },
      },
      disabled: {},
    },
    transparent: {
      true: {
        'backgroundColor': 'transparent',
        'color': '$gray500',
        'borderColor': '$gray200',
        'path': {
          stroke: '$gray500',
        },
        '&:hover': {
          backgroundColor: 'transparent',
          borderColor: '$gray400',
          color: '$gray600',
          path: {
            stroke: '$gray600',
          },
        },
      },
      asTransparentGray: {
        'backgroundColor': 'transparent',
        'color': '$gray500',
        'borderColor': '$gray600',
        'path': {
          stroke: '$gray500',
        },
        '&:hover': {
          backgroundColor: 'transparent',
          borderColor: '$primary800',
          color: '$gray700',
          path: {
            stroke: '$gray700',
          },
        },
      },
      asAnchor: {
        'alignItems': 'center',
        ...anchorStyles,
        'p': 0,
        'backgroundColor': 'transparent',
        'border': 'none',
        'fontSize': '$16',
        'fontWeight': '$semibold',
        '&:hover': {
          backgroundColor: 'transparent',
          color: '$primaryBgHover',
        },
      },
      asIcon: {
        'alignItems': 'center',
        'p': 0,
        'backgroundColor': 'transparent',
        'border': 'none',
        'color': '$primaryBase',
        'path': {
          stroke: '$primaryBase',
        },
        '&:hover': {
          backgroundColor: 'transparent',
          color: '$primaryBgHover',
          path: {
            stroke: '$primaryBgHover',
          },
        },
      },
      asIconColor: {
        'alignItems': 'center',
        'p': 0,
        'backgroundColor': 'transparent',
        'border': 'none',
        '&:hover': {
          backgroundColor: 'transparent',
        },
      },
      asIconBlack: {
        'alignItems': 'center',
        'p': 0,
        'backgroundColor': 'transparent',
        'border': 'none',
        'color': '$titleGray',
        'path': {
          stroke: '$titleGray',
        },
        '&:hover': {
          backgroundColor: 'transparent',
          color: '$primaryBase',
          path: {
            stroke: '$primaryBase',
          },
        },
      },
      asIconPrimary: {
        'alignItems': 'center',
        'p': 0,
        'backgroundColor': 'transparent',
        'border': 'none',
        'color': '$primaryBase',
        'path': {
          stroke: '$primaryBase',
        },
        '&:hover': {
          backgroundColor: 'transparent',
          color: '$primaryBgHover',
          path: {
            stroke: '$primaryBgHover',
          },
        },
      },
      asIconGray: {
        'alignItems': 'center',
        'p': 0,
        'backgroundColor': 'transparent',
        'border': 'none',
        'color': '$defaultIcon',
        'path': {
          stroke: '$defaultIcon',
        },
        '&:hover': {
          backgroundColor: 'transparent',
          color: '$titleGray',
          path: {
            stroke: '$titleGray',
          },
        },
      },
      asIconError: {
        'alignItems': 'center',
        'p': 0,
        'backgroundColor': 'transparent',
        'border': 'none',
        'color': '$errorBgHover',
        'path': {
          stroke: '$errorBgHover',
        },
        '&:hover': {
          backgroundColor: 'transparent',
          color: '$error',
          path: {
            stroke: '$error',
          },
        },
      },
      asFillIcon: {
        'alignItems': 'center',
        'p': 6,
        'backgroundColor': '$primaryBase',
        'color': '$primaryBase',
        'borderRadius': '$rounded',
        'path': {
          stroke: '$whiteBase',
        },
        '&:hover': {
          backgroundColor: '$primaryBgHover',
          color: '$primaryBgHover',
          path: {
            stroke: '$whiteBase',
          },
        },
      },
      asTagIcon: {
        'alignItems': 'center',
        'backgroundColor': 'transparent',
        'color': '$titleGray',
        'borderRadius': '$radii$md',
        'p': '$space$2 $space$10',
        'borderColor': '$gray300',
        'maxHeight': 28,
        '&:hover': {
          backgroundColor: 'transparent',
          color: '$primaryBase',
        },
      },
    },
  },
  'defaultVariants': {
    size: 'sm',
    color: 'primary',
  },
})

const spin = keyframes({
  to: {
    transform: 'rotate(359deg)',
  },
})

const grow = keyframes({
  to: {
    width: '$size$14',
    height: '$size$14',
  },
})

const Loader = styled('div', {
  width: 0,
  height: 0,
  border: '.25rem solid $blackBase',
  borderLeftColor: '$whiteBase',
  borderRadius: '50%',
  mr: '$space$12',
  animation: `${spin} .6s infinite linear, ${grow} .3s forwards ease-out`,
})

export type ButtonShowAsProps =
  | 'true'
  | 'asTransparentGray'
  | 'asIcon'
  | 'asIconError'
  | 'asIconBlack'
  | 'asIconPrimary'
  | 'asIconGray'
  | 'asAnchor'
  | 'asFillIcon'
  | 'asIconColor'
  | 'asTagIcon'
export type StateProps =
  | 'active'
  | 'disabled'
  | 'delete'
  | 'primaryDisabled'
  | 'grayDisabled'
  | 'errorDisabled'
export type ColorProps =
  | 'primary'
  | 'secondary'
  | 'gray'
  | 'success'
  | 'warning'
  | 'error'
  | 'transparent'
  | 'black'
  | 'white'
  | 'disabled'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  color?: ColorProps | 'dropdown' | 'errorDropdown' | undefined | 'primaryLight'
  state?: StateProps
  iconButton?: ColorProps
  transparent?: ButtonShowAsProps | undefined
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  css?: CSS
  startIcon?: ReactNode
  endIcon?: ReactNode
  disabled?: boolean
  loading?: {
    text: string
    enable: boolean
  }
  children?: ReactNode
  handleclick?: (props: number | string | boolean) => void | undefined
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (props, ref) => {
    const { startIcon, endIcon, children, disabled, loading, color, ...rest } =
      props

    const showLoading = loading?.enable || false
    // const state = showLoading || disabled ? 'disabled' : undefined
    const state =
      showLoading || disabled
        ? color === 'primary'
          ? 'primaryDisabled'
          : color === 'gray'
          ? 'grayDisabled'
          : color === 'error'
          ? 'errorDisabled'
          : 'disabled'
        : undefined

    const iconStyle = css({
      ...(startIcon && { marginRight: '$space$8', alignSelf: 'center' }),
      ...(endIcon && { marginLeft: '$space$8', alignSelf: 'center' }),
    })

    return (
      <ButtonCss
        {...rest}
        ref={ref}
        disabled={showLoading || disabled}
        state={state}
        color={color}
      >
        {showLoading && <Loader />}
        {showLoading && loading?.text && <>{loading.text}</>}
        {!showLoading &&
          startIcon &&
          cloneElement(startIcon as ReactElement, {
            className: iconStyle.toString(),
          })}
        {!showLoading && children}
        {!showLoading &&
          endIcon &&
          cloneElement(endIcon as ReactElement, {
            className: iconStyle.toString(),
          })}
      </ButtonCss>
    )
  }
)
